import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Loader2, Mail, Lock, Eye, EyeOff, CheckCircle, Fingerprint, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Brand Colors from Catavento Logo
const BRAND_COLORS = {
  coral: '#E8A598',
  blue: '#7EB3D8', 
  yellow: '#F0E68C',
  green: '#7FBF7F',
  pearl: '#E2E4E9',
};

// URL da logomarca oficial
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_clinical-scheduler/artifacts/dh1ga4hw_LOGO_interequipe-removebg_sem%20nome.png';

// Official Logo Component - Using the REAL logo image
const OfficialLogo = ({ size = 80 }) => (
  <img 
    src={LOGO_URL}
    alt="InterEquipe"
    style={{ width: size, height: 'auto', objectFit: 'contain' }}
  />
);

const Login = () => {
  const navigate = useNavigate();
  const { signIn, resetPassword, loading, error, clearError } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para recuperação de senha
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  // Estados para WebAuthn (biometria)
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricOption, setShowBiometricOption] = useState(false);

  // Verificar se WebAuthn está disponível no navegador
  useEffect(() => {
    const checkWebAuthn = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricAvailable(available);
        } catch (e) {
          console.log('WebAuthn check failed:', e);
        }
      }
    };
    checkWebAuthn();
  }, []);

  // Verificar se o email tem biometria cadastrada
  useEffect(() => {
    const checkBiometric = async () => {
      if (email && email.includes('@') && biometricAvailable) {
        try {
          const res = await axios.get(`${API}/api/auth/webauthn/check/${encodeURIComponent(email)}`);
          setShowBiometricOption(res.data.available);
        } catch (e) {
          setShowBiometricOption(false);
        }
      } else {
        setShowBiometricOption(false);
      }
    };
    
    const timeoutId = setTimeout(checkBiometric, 500);
    return () => clearTimeout(timeoutId);
  }, [email, biometricAvailable]);

  // Login via biometria
  const handleBiometricLogin = async () => {
    if (!email) {
      setLocalError('Por favor, informe seu e-mail primeiro');
      return;
    }
    
    setBiometricLoading(true);
    setLocalError('');
    
    try {
      // 1. Obter challenge do servidor
      const challengeRes = await axios.post(`${API}/api/auth/webauthn/login-challenge?email=${encodeURIComponent(email)}`);
      const { challenge, allowCredentials } = challengeRes.data;
      
      // 2. Converter dados para formato WebAuthn
      const publicKeyCredentialRequestOptions = {
        challenge: Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        allowCredentials: allowCredentials.map(cred => ({
          ...cred,
          id: Uint8Array.from(atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
        })),
        timeout: 60000,
        userVerification: 'required'
      };
      
      // 3. Solicitar autenticação biométrica
      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });
      
      // 4. Enviar resposta para o servidor
      const loginRes = await axios.post(`${API}/api/auth/webauthn/login`, {
        credential_id: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''),
        authenticator_data: btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))),
        client_data_json: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
        signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature)))
      });
      
      if (loginRes.data.success) {
        toast.success('Login biométrico realizado!');
        // O backend retorna que precisamos criar sessão via Supabase
        // Por enquanto, redirecionamos para que o usuário faça login normal
        // Em uma implementação completa, criaríamos um token JWT customizado
        navigate('/dashboard');
      }
      
    } catch (err) {
      console.error('Biometric login error:', err);
      if (err.name === 'NotAllowedError') {
        setLocalError('Autenticação biométrica cancelada');
      } else if (err.response?.data?.detail) {
        setLocalError(err.response.data.detail);
      } else {
        setLocalError('Erro na autenticação biométrica. Tente o login normal.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // Limpar qualquer erro antigo quando a página de login carrega
  useEffect(() => {
    if (clearError) {
      clearError();
    }
  }, [clearError]);

  // Handler para recuperação de senha
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    
    if (!resetEmail) {
      setResetError('Por favor, informe seu e-mail');
      return;
    }
    
    setResetLoading(true);
    
    const result = await resetPassword(resetEmail);
    
    if (result.success) {
      setResetSuccess(true);
    } else {
      setResetError(result.error || 'Erro ao enviar email de recuperação');
    }
    
    setResetLoading(false);
  };

  // Fechar modal e resetar estados
  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);

    if (!email || !password) {
      setLocalError('Por favor, preencha todos os campos');
      setIsSubmitting(false);
      return;
    }

    const result = await signIn(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FAFAFA' }}>
      {/* Abstract Background - Subtle brand colors */}
      <div 
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{ opacity: 0.06 }}
      >
        <div 
          className="absolute rounded-full blur-3xl"
          style={{ 
            width: '600px', 
            height: '600px', 
            top: '-200px', 
            right: '-200px',
            backgroundColor: BRAND_COLORS.coral 
          }} 
        />
        <div 
          className="absolute rounded-full blur-3xl"
          style={{ 
            width: '500px', 
            height: '500px', 
            bottom: '-150px', 
            left: '-150px',
            backgroundColor: BRAND_COLORS.blue 
          }} 
        />
        <div 
          className="absolute rounded-full blur-3xl"
          style={{ 
            width: '400px', 
            height: '400px', 
            top: '40%', 
            left: '30%',
            backgroundColor: BRAND_COLORS.green 
          }} 
        />
      </div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-md space-y-8">
          {/* Logo/Brand - OFFICIAL LOGO */}
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <OfficialLogo size={180} />
            </div>
            <p 
              className="mt-2 text-base"
              style={{ color: '#71717A' }}
            >
              Sistema de Gestão Clínica
            </p>
          </div>

          {/* Login Card */}
          <Card 
            className="border shadow-sm backdrop-blur-sm"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.8)', 
              borderColor: '#E2E4E9',
              borderRadius: '1.5rem'
            }}
          >
            <CardHeader className="space-y-1 pb-4">
              <CardTitle 
                className="text-2xl font-semibold"
                style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}
              >
                Entrar
              </CardTitle>
              <CardDescription style={{ color: '#71717A' }}>
                Acesse com suas credenciais
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {(localError || error) && (
                <Alert 
                  variant="destructive" 
                  className="border"
                  style={{ 
                    backgroundColor: `${BRAND_COLORS.coral}15`, 
                    borderColor: BRAND_COLORS.coral,
                    borderRadius: '0.75rem'
                  }}
                >
                  <AlertDescription style={{ color: '#7A3527' }}>
                    {localError || error}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label 
                    htmlFor="email"
                    className="text-sm font-medium"
                    style={{ color: '#52525B' }}
                  >
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail 
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: '#A1A1AA' }}
                    />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 transition-all focus:ring-2"
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E4E9',
                        borderRadius: '0.75rem',
                        color: '#18181B'
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label 
                    htmlFor="password"
                    className="text-sm font-medium"
                    style={{ color: '#52525B' }}
                  >
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock 
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: '#A1A1AA' }}
                    />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-12 transition-all focus:ring-2"
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E4E9',
                        borderRadius: '0.75rem',
                        color: '#18181B'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-zinc-100 transition-colors"
                      style={{ color: '#A1A1AA' }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  className="w-full h-12 font-medium transition-all hover:opacity-90 hover:scale-[1.01]"
                  style={{ 
                    backgroundColor: '#18181B',
                    color: '#FFFFFF',
                    borderRadius: '9999px'
                  }}
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>

              {/* Botão de Login Biométrico */}
              {biometricAvailable && showBiometricOption && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" style={{ borderColor: '#E2E4E9' }} />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="px-2 text-muted-foreground" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', color: '#A1A1AA' }}>
                        ou
                      </span>
                    </div>
                  </div>
                  
                  <Button
                    data-testid="biometric-login-button"
                    type="button"
                    variant="outline"
                    className="w-full h-12 font-medium transition-all hover:scale-[1.01] gap-2"
                    style={{ 
                      borderColor: BRAND_COLORS.green,
                      color: '#2C3632',
                      borderRadius: '9999px',
                      backgroundColor: `${BRAND_COLORS.green}15`
                    }}
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                  >
                    {biometricLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <Fingerprint className="h-5 w-5" style={{ color: BRAND_COLORS.green }} />
                        Entrar com Digital
                      </>
                    )}
                  </Button>
                </>
              )}

              {/* Link para recuperação de senha */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(email);
                    setForgotPasswordOpen(true);
                  }}
                  className="text-sm hover:underline transition-colors"
                  style={{ color: '#52525B' }}
                >
                  Esqueceu sua senha?
                </button>
              </div>

              <p 
                className="text-center text-xs pt-2"
                style={{ color: '#A1A1AA' }}
              >
                Acesso restrito a usuários autorizados
              </p>
            </CardContent>
          </Card>

          {/* Footer info */}
          <div className="text-center space-y-3">
            {/* Specialties with colored dots */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.coral }} />
                <span className="text-xs" style={{ color: '#71717A' }}>Psicologia</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.blue }} />
                <span className="text-xs" style={{ color: '#71717A' }}>Fonoaudiologia</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.green }} />
                <span className="text-xs" style={{ color: '#71717A' }}>Terapia Ocupacional</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.yellow }} />
                <span className="text-xs" style={{ color: '#71717A' }}>Nutrição</span>
              </div>
            </div>
            <p 
              className="text-xs"
              style={{ color: '#A1A1AA' }}
            >
              Sorocaba/SP, Brasil
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Hero with brand illustration */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative"
        style={{ backgroundColor: '#F4F4F5' }}
      >
        {/* Brand colored circles in background */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute rounded-full opacity-20"
            style={{ 
              width: '300px', 
              height: '300px', 
              top: '10%', 
              right: '10%',
              backgroundColor: BRAND_COLORS.coral 
            }} 
          />
          <div 
            className="absolute rounded-full opacity-20"
            style={{ 
              width: '250px', 
              height: '250px', 
              bottom: '20%', 
              left: '5%',
            backgroundColor: BRAND_COLORS.blue 
            }} 
          />
          <div 
            className="absolute rounded-full opacity-20"
            style={{ 
              width: '200px', 
              height: '200px', 
              top: '50%', 
              left: '40%',
              backgroundColor: BRAND_COLORS.green 
            }} 
          />
          <div 
            className="absolute rounded-full opacity-15"
            style={{ 
              width: '180px', 
              height: '180px', 
              bottom: '10%', 
              right: '20%',
              backgroundColor: BRAND_COLORS.yellow 
            }} 
          />
        </div>

        <div className="relative z-10 text-center max-w-lg">
          <div className="flex justify-center mb-8">
            <OfficialLogo size={280} />
          </div>
          <h2 
            className="text-3xl font-semibold mb-4"
            style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}
          >
            Cuidado Multidisciplinar
          </h2>
          <p 
            className="text-lg mb-6"
            style={{ color: '#52525B' }}
          >
            Atendimento integrado com profissionais especializados em diversas áreas da saúde.
          </p>
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
          >
            <span className="text-sm" style={{ color: '#52525B' }}>
              ABA • Integração Sensorial • Convencional
            </span>
          </div>
        </div>
      </div>

      {/* Modal de Recuperação de Senha */}
      <Dialog open={forgotPasswordOpen} onOpenChange={handleCloseForgotPassword}>
        <DialogContent className="sm:max-w-md" style={{ borderRadius: '1.5rem' }}>
          <DialogHeader>
            <DialogTitle 
              className="text-xl font-semibold"
              style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}
            >
              Recuperar Senha
            </DialogTitle>
            <DialogDescription style={{ color: '#71717A' }}>
              {resetSuccess 
                ? 'Email enviado com sucesso!'
                : 'Digite seu e-mail para receber as instruções de recuperação.'
              }
            </DialogDescription>
          </DialogHeader>

          {resetSuccess ? (
            <div className="py-6 text-center">
              <div 
                className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `${BRAND_COLORS.green}30` }}
              >
                <CheckCircle className="h-8 w-8" style={{ color: '#266126' }} />
              </div>
              <p className="text-sm" style={{ color: '#52525B' }}>
                Enviamos um link de recuperação para <strong>{resetEmail}</strong>.
              </p>
              <p className="text-xs mt-2" style={{ color: '#A1A1AA' }}>
                Verifique sua caixa de entrada e spam.
              </p>
              <Button
                onClick={handleCloseForgotPassword}
                className="mt-6 w-full"
                style={{ backgroundColor: '#18181B', borderRadius: '9999px' }}
              >
                Voltar ao Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4 py-2">
              {resetError && (
                <Alert 
                  variant="destructive" 
                  className="border"
                  style={{ 
                    backgroundColor: `${BRAND_COLORS.coral}15`, 
                    borderColor: BRAND_COLORS.coral,
                    borderRadius: '0.75rem'
                  }}
                >
                  <AlertDescription style={{ color: '#7A3527' }}>
                    {resetError}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label 
                  htmlFor="reset-email"
                  className="text-sm font-medium"
                  style={{ color: '#52525B' }}
                >
                  E-mail
                </Label>
                <div className="relative">
                  <Mail 
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                    style={{ color: '#A1A1AA' }}
                  />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="pl-10 h-12"
                    style={{ 
                      backgroundColor: '#FFFFFF',
                      borderColor: '#E2E4E9',
                      borderRadius: '0.75rem',
                      color: '#18181B'
                    }}
                    autoFocus
                  />
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseForgotPassword}
                  className="w-full sm:w-auto"
                  style={{ borderRadius: '9999px' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto"
                  style={{ 
                    backgroundColor: '#18181B',
                    borderRadius: '9999px'
                  }}
                  disabled={resetLoading}
                >
                  {resetLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Link'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
